<template>
  <div
    :class="className"
    @click="onCardClick"
    :id="id">
    <div class="book-card-vue__container">
      <figure
        class="book-card-vue__figure">
        <pict
          block-name="book-card-vue"
          :sources="image.sources"
          :default="image.default" />
      </figure>
      <div class="book-card-vue__content">
        <div class="book-card-vue__body">
          <h4
            class="book-card-vue__headline"
            v-html="title" />
          <div
            class="book-card-vue__author"
            v-html="author" />
          <ul class="book-card-vue__stats">
            <li
              v-for="(item, key) in stats"
              :key="key"
              class="book-card-vue__stats-item">
              <sprite
                :id="item.sprite"
                class="book-card-vue__stats-sprite" />
              <span class="book-card-vue__stats-label">
                {{ item.label }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sprite from "/shared/sprite";
import Pict from "/shared/pict";
import "/wds/sprites/app/time.svg";
import "/wds/sprites/app/audio.svg";
import events from "/events";

export const messages = {
  en: {
    duration: "{duration}-minute read",
    audio: "Audio available"
  },
  de: {
    duration: "{duration} Minuten",
    audio: "Audio-Version verfügbar"
  }
};

export default {
  name: "BookCard",
  components: {
    Sprite,
    Pict
  },
  props: {
    id: { type: String, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, required: true },
    image: { type: Object, required: true },
    author: { type: String, required: true },
    readingDuration: { type: [String, Number], required: true },
    audioUrl: { type: String, default: null },
    previewUrl: { type: String, default: null },
    isBookLinked: { type: Boolean, default: false }
  },
  computed: {
    className() {
      return {
        "book-card-vue": true,
        "book-card-vue--linked": this.isBookClickable
      };
    },
    isBookClickable() {
      return this.isBookLinked && this.previewUrl;
    },
    stats() {
      const stats = [];
      stats.push({
        label: this.$t("duration", { duration: this.readingDuration }),
        sprite: "app/time"
      });
      if (this.audioUrl) {
        stats.push({
          label: this.$t("audio"),
          sprite: "app/audio"
        });
      }
      return stats;
    }
  },
  methods: {
    onCardClick() {
      this.$emit("click", this.id);
      events.track("body-20190919-book_card", "1-click", this.id);
      if (this.isBookClickable){
        window.location.assign(this.previewUrl);
      }
    }
  },
  i18n: { messages }
};
</script>

<style lang="scss">
@import "../../../assets/stylesheets/base/mixins/truncation_gradient";

.book-card-vue {
  $max-lines: 2;

  width: 100%;
  max-width: $x-wide-block-width;

  &__container {
    position: relative;

    display: flex;
    flex-grow: 1;
    flex-direction: column;
    width: 100%;

    background: $white;
  }

  &__figure {
    position: relative;

    overflow: hidden;

    border-top-left-radius: $border-radius-4;
    border-top-right-radius: $border-radius-4;
  }

  &__picture {
    position: relative;

    display: block;
    width: 100%;
    padding-top: 100%;
  }

  &__image {
    position: absolute;
    top: 0;

    display: block;
    width: 100%;
    height: 100%;

    object-fit: cover;
  }

  &__content {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    width: 100%;

    border: 1px solid $grey-light;
    border-top: 0;
    border-bottom-right-radius: $border-radius-4;
    border-bottom-left-radius: $border-radius-4;
  }

  &__body {
    flex-grow: 1;
    width: 100%;
    padding: $basex3 $basex2 $basex2;

    word-wrap: break-word;
  }

  &__headline {
    $style: h4;

    @include truncation-gradient(font-size($style) * line-height($style) * ($max-lines - 1));
    @include font-style($style);
    @include font-color($style);

    position: relative;

    width: 100%;
    max-height: font-size($style) * line-height($style) * $max-lines;
    margin-bottom: $base;
    overflow: hidden;
  }

  &__author {
    @include font-style(subheadline, $color: $grey);

    margin-bottom: $spacing-8;
  }

  &__stats {
    @include font-style(caption);

    display: flex;
    flex-wrap: wrap;

    color: $grey-dark;
  }

  &__stats-item {
    display: flex;
    flex-grow: 1;
    flex-shrink: 0;
    align-items: center;
    margin-bottom: $base;

    white-space: nowrap;

    &:nth-child(2n+1) {
      margin-right: $basex2;
    }
  }

  &__stats-sprite {
    width: 14px;
    height: 14px;
    margin-right: $base;
  }

  &--linked {
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
